import React, { ReactNode } from "react";

import { createSetVisibilityAction, GridLayoutMultiwebItem } from "@multiweb/frames";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { bridge } from "@multiweb/reactCore/bridge";
import { AnyConstructor, Mixin } from "Global/Base/types";
import { IHTMLElementWithControl, IVisible } from "UI/UI/UI/Controls/ControlInterfaces";
import { IItemRenderer } from "../types";

const { dispatchAction } = bridge;

const logger = LazyLoadingSectionLogger.create(LoggingSection.gridLayout);

/**
 * Mixin, který přidává kontrolu viditelnosti GeneralGridLayoutRendereru.
 *
 * @param Base Base třída rozšiřující GeneralGridLayoutItemRenderer.
 */
export const HasVisibilityChanged = <T extends AnyConstructor<IItemRenderer>>(Base: T): T =>
    class HasVisibilityChanged extends Base {
        public _listener: (control: any, value: boolean) => void = (_control, value) => {
            dispatchAction(createSetVisibilityAction(this.innerElement.id, value));
        };

        public _getVisibleControl(): IVisible | undefined {
            if (this.innerElement instanceof IHTMLElementWithControl && this.innerElement?.tscControl instanceof IVisible) {
                return this.innerElement.tscControl;
            }
            return undefined;
        }

        public _getVisibility(): boolean | undefined {
            return this._getVisibleControl()?.visible;
        }

        public _addVisibilityChangedListener(): void {
            const visibleControl = this._getVisibleControl();
            dispatchAction(createSetVisibilityAction(this.innerElement.id, !!this._getVisibility()));
            if (visibleControl) {
                visibleControl.visibilityChanged.add(this._listener);
            } else {
                logger.error(`Can not find element with id="${this.innerElement.id}"`);
            }
        }

        public _removeVisibilityChangedListener(): void {
            const visibleControl = this._getVisibleControl();
            if (visibleControl) {
                visibleControl.visibilityChanged.remove(this._listener);
            } else {
                logger.error(`Can not find element with id="${this.innerElement.id}"`);
            }
        }

        public override render(): ReactNode {
            return (
                <GridLayoutMultiwebItem
                    key={this.reactKey}
                    frameKey={this.innerElement.id}
                    {...this.gridItemProps}
                >
                    {this.getMountedInnerElement()}
                </GridLayoutMultiwebItem>
            );
        }

        public override initialize(): void {
            this._addVisibilityChangedListener();
        }

        public override dispose(): void {
            this._removeVisibilityChangedListener();
            super.dispose();
        }
    };

export type HasVisibilityChanged = Mixin<typeof HasVisibilityChanged>;
